import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation, ChangeDetectionStrategy,
} from '@angular/core';
import {of, Subject} from 'rxjs';
import {debounceTime, distinctUntilChanged, map, switchMap, takeWhile, tap} from 'rxjs/operators';

import {Address} from '../../../models/address.model';
import {AddressService} from '../../../services/address.service';
import {AuthService} from '../../../services/auth.service';
import {OrderItemDelivery} from '../../../models/order-item-delivery.model';
import {Entity} from '../../../models/entity.model';
import {ModalService} from '../../../services/modal.service';
import {computeShipmentLabel, sortAddresses} from '../../../shared/helpers';
import {OrderItem} from '../../../models/order-item.model';
import {Order} from '../../../models/order.model';
import {Product} from '../../../models/product.model';
import {MobileCheckServiceService} from '../../../services/mobile-check-service.service';
import {AddressType} from '../../../enums/address-type';
import { OrderItemService } from '../../../services/order-item.service';
import {NgbPopover} from '@ng-bootstrap/ng-bootstrap';


const DROPDOWN_WIDTH = 300;

@Component({
    selector: 'app-ship-to-address',
    templateUrl: './ship-to-address.component.html',
    styleUrls: ['./ship-to-address.component.scss'],
    encapsulation: ViewEncapsulation.None,
    standalone: false,
    changeDetection: ChangeDetectionStrategy.Eager
})
export class ShipToAddressComponent
  implements OnInit, OnDestroy, AfterViewInit {
  private alive = true;

  addr_id: string = null;
  addressesSubject: Subject<string> = new Subject<string>();
  addressesLoading = false;
  computedAddressList: Address[] = [];
  itemsTemplateHeader = '';
  addressType = AddressType;

  private initialized;
  private _orderItem: OrderItem;

  _entity: Entity;
  entityLabel: string;

  @ViewChild('select', { static: false }) addressSelector;

  @Input() disabled = false;

  @Input() delivery: OrderItemDelivery;

  @Input() isInline = false;

  @Input() className = '';

  @Input() order: Order;

  // output event when address has been selected
  @Output() addressSelected = new EventEmitter<Address>();

  @Output() eudrRestrictionChanged = new EventEmitter<boolean>();
  @Input() set orderItem(val: OrderItem) {
    this._orderItem = val;
  }

  constructor(
    private addressService: AddressService,
    private authService: AuthService,
    private modalService: ModalService,
    private mobileCheck: MobileCheckServiceService,
    private orderItemService: OrderItemService,
  ) { }

  ngOnInit(): void {
    this.addressesSubject
      .pipe(
        takeWhile(() => this.alive),
        debounceTime(200),
        distinctUntilChanged(),
        tap(() => this.addressesLoading = true),
        switchMap(term => {
          if (!term) {
            return of(this.availableAddresses);
          }
          return this.addressService.searchAddressesByTerm({
            match: term,
            product_id: this.productId,
            excludeWarehouse: !this.canHaveWarehouseAddresses,
          }).pipe(
            map( retResult => {
              return retResult || [];
            })
          )
      }))
      .subscribe( result => {
        this.addressesLoading = false;
        this.computedAddressList = sortAddresses(result);
      });

    this.computedAddressList = this.availableAddresses;

    if (this.delivery) {
      this.addr_id = this.delivery.uniqueAddressId;
    } else {
      this.addr_id = null;
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => (this.initialized = true));
  }

  ngOnDestroy(): void {
    this.alive = false;
    clearTimeout(this.dlPopoverCloseTimer);
    this.dlPopoverRef?.close();
  }

  onSelect(data): void {
    if (data instanceof Event) {
      return;
    }
    const selectedAddress: Address = data;

    // Update EUDR state based on selected address
    this.orderItemService.updateEudrStateForWholeItem(this._orderItem);
    const hasRestriction = this.orderItemService.isEudrRestricted(this._orderItem);
    this.eudrRestrictionChanged.emit(hasRestriction);

    this.addressSelected.emit(selectedAddress);
  }

  onCloseDropdown(): void {
    // reset search items to default state
    this.resetAddressList(this.availableAddresses);
  }

  resetAddressList(addresses: Address[] = []) {

    this.computedAddressList = [...addresses];

  }


  addAddress() {
    this.modalService.editAddress(null, true).then((result) => {
      if (result) {
        this.resetAddressList(this.availableAddresses);
        this.addr_id = result.uniqueAddressId;
        this.onSelect(result);
      }
    });
    this.addressSelector.close();
    return false;
  }

  get addressLabel() {
    if (!this.delivery) {
      return '';
    }

    return computeShipmentLabel(this.delivery);
  }

  get product(): Product {
    return this._orderItem?.product;
  }

  get productId(): number {
    return this.product?.id;
  }

  get isEmployee(): boolean {
    return this.authService.isEmployee;
  }

  get isDecisionPoint(): boolean {
    return this.order.isDecisionPoint;
  }

  get attention(): string {
    return this.order?.attr_attention || '';
  }

  get wslrEntity(): Entity {
    return this.order.isWSLR ? this.order?.entity : null;
  }

  get deliveries(): OrderItemDelivery[] {
    return this._orderItem?.deliveries || [];
  }

  get canAddAddresses(): boolean {
    return this.authService.canAddAddress;
  }

  get mobile(): boolean {
    return this.mobileCheck.mobile;
  }

  get dropDownClasses(): string {
    if (!this.mobile || !this.initialized) {
      return `${this.className}`;
    }

    let leftAligned = false;
    if (this.addressSelector) {
      const rect = this.addressSelector.element.getBoundingClientRect();
      leftAligned = rect.right >= DROPDOWN_WIDTH;
    }

    return `${this.className} ${this.mobile ? 'mobile ' : 0} ${
      leftAligned ? 'left-aligned' : ''
    }`;
  }

  get  disableDropdown(): boolean {
    return this.disabled || (this.order.hasDistributionList && !this.delivery.isAdditional);
  }

  get isDistributionListAddress(): boolean {
    return this.order.hasDistributionList && !this.delivery.isAdditional;
  }

  private dlPopoverCloseTimer: ReturnType<typeof setTimeout>;
  private dlPopoverRef: NgbPopover;

  openDlPopover(popover: NgbPopover): void {
    this.dlPopoverRef = popover;
    clearTimeout(this.dlPopoverCloseTimer);
    popover.open();
  }

  scheduleDlPopoverClose(popover: NgbPopover): void {
    clearTimeout(this.dlPopoverCloseTimer);
    this.dlPopoverCloseTimer = setTimeout(() => {
      this.dlPopoverCloseTimer = undefined;
      popover.close();
    }, 300);
  }

  cancelDlPopoverClose(): void {
    clearTimeout(this.dlPopoverCloseTimer);
  }

  scheduleCloseDlPopoverFromContent(): void {
    if (this.dlPopoverRef) {
      this.scheduleDlPopoverClose(this.dlPopoverRef);
    }
  }

  get availableAddresses(): Address[] {
    if (this.order?.hasDistributionList && !this.delivery.isAdditional) {
      return this.order.distributionList.addresses || [];
    }

    return this.canHaveWarehouseAddresses ?
      this.addressService.allShipmentAddressesAndWarehouses
      : this.addressService.allShipmentAddresses;
  }

  // check whether warehouse addresses can be shown
  // conditions: product has enabled allocated inventory and order is in buying window
  get canHaveWarehouseAddresses(): boolean {
    return this.product?.isEnabledAllocatedInventory && this.order?.isByingWindow;
  }

}
